import { Box, Button, CheckboxInput, Modal, TextInput, toast } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import type { SyntheticEvent } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';

import type { MonthlyMultipliers } from 'types';

import {
  useCreateSeasonalPricingPlanMutation,
  useCreateSeasonalPricingPlanVersionMutation,
} from '../../../services/pricing';
import type { SeasonalPricingPlan } from '../../../services/pricing';

import { ANNUAL_EQUIVALENT_TOTAL, MONTH_KEYS, MONTH_LABELS, sumMultipliers } from './constants';
import type { MonthKey } from './constants';

/** Month values and the admin fee are held as strings so a half-typed or cleared input stays editable. */
export interface SeasonalPlanFormValues {
  name: string;
  multipliers: Record<MonthKey, string>;
  adminFeeMonthly: string;
  acknowledgeException: boolean;
}

interface SeasonalPlanModalProps {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  programType: string;
  /** Present when publishing a new version of an existing plan group. */
  planGroupId?: string;
  defaultValues?: SeasonalPlanFormValues;
  /** Called after a new plan is created so the caller can navigate to its detail page. */
  onSuccess?: (planGroupId: string) => void;
}

export const buildDefaultFormValues = (): SeasonalPlanFormValues => ({
  name: '',
  multipliers: MONTH_KEYS.reduce<Partial<Record<MonthKey, string>>>((acc, key) => {
    acc[key] = '100';
    return acc;
  }, {}) as Record<MonthKey, string>,
  adminFeeMonthly: '',
  acknowledgeException: false,
});

/**
 * Seeds the new-version form from the version it supersedes. The acknowledgement is deliberately
 * not carried over — each version records its own acknowledger.
 */
export const toFormValues = (plan: SeasonalPricingPlan): SeasonalPlanFormValues => ({
  name: plan.name,
  multipliers: MONTH_KEYS.reduce<Partial<Record<MonthKey, string>>>((acc, key) => {
    acc[key] = String(plan.monthlyMultipliers[key]);
    return acc;
  }, {}) as Record<MonthKey, string>,
  adminFeeMonthly: plan.adminFeeMonthly === undefined ? '' : String(plan.adminFeeMonthly),
  acknowledgeException: false,
});

const MonthMultiplierInput = ({
  monthKey,
  control,
}: {
  monthKey: MonthKey;
  control: Control<SeasonalPlanFormValues>;
}) => (
  <Controller
    name={`multipliers.${monthKey}`}
    control={control}
    rules={{
      required: 'Required',
      validate: (value: string) => {
        const parsed = Number(value);
        if (!Number.isInteger(parsed)) return 'Whole numbers only';
        if (parsed < 1) return 'Must be at least 1';
        return true;
      },
    }}
    render={({ field, fieldState }) => (
      <TextInput
        id={`multiplier-${monthKey}`}
        label={MONTH_LABELS[monthKey]}
        suffix="%"
        inputMode="numeric"
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        error={fieldState.error?.message}
      />
    )}
  />
);

/** Subscribes on its own so a keystroke re-renders the running total instead of all twelve inputs. */
const AnnualTotalSummary = ({ control }: { control: Control<SeasonalPlanFormValues> }) => {
  const multipliers = useWatch({ control, name: 'multipliers' });
  const total = sumMultipliers(multipliers);
  const isEquivalent = total === ANNUAL_EQUIVALENT_TOTAL;

  return (
    <Box direction="column" alignItems="flex-end" gap="2xs" fontSize="sm">
      <Box>
        Annual total {total}% of {ANNUAL_EQUIVALENT_TOTAL}%
      </Box>
      <Box fontWeight="medium" color={isEquivalent ? 'success' : 'danger'}>
        {isEquivalent
          ? 'Annual-equivalent'
          : `Not annual-equivalent (${total > ANNUAL_EQUIVALENT_TOTAL ? 'over' : 'under'} by ${Math.abs(
              total - ANNUAL_EQUIVALENT_TOTAL,
            )}%)`}
      </Box>
    </Box>
  );
};

/** Only rendered while the schedule is off target; the rule reads sibling values so it never validates against a stale total. */
const ExceptionAcknowledgement = ({ control }: { control: Control<SeasonalPlanFormValues> }) => {
  const multipliers = useWatch({ control, name: 'multipliers' });

  if (sumMultipliers(multipliers) === ANNUAL_EQUIVALENT_TOTAL) return null;

  return (
    <Controller
      name="acknowledgeException"
      control={control}
      rules={{
        validate: (value: boolean, formValues: SeasonalPlanFormValues) =>
          sumMultipliers(formValues.multipliers) === ANNUAL_EQUIVALENT_TOTAL ||
          value ||
          'You must acknowledge this plan before saving',
      }}
      render={({ field, fieldState }) => (
        <CheckboxInput
          id="acknowledgeException"
          label="I acknowledge this plan is not annual-equivalent — a full year costs more or less than the standard flat payment"
          isChecked={field.value}
          onChange={(event) => field.onChange(event.target.checked)}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
        />
      )}
    />
  );
};

export function SeasonalPlanModal({
  isModalOpen,
  closeModal,
  programType,
  planGroupId,
  defaultValues,
  onSuccess,
}: SeasonalPlanModalProps) {
  const isVersionMode = Boolean(planGroupId);

  const [createPlan, { isLoading: isCreating }] = useCreateSeasonalPricingPlanMutation();
  const [createVersion, { isLoading: isVersioning }] = useCreateSeasonalPricingPlanVersionMutation();
  const isSubmitting = isCreating || isVersioning;

  const { control, handleSubmit, reset } = useForm<SeasonalPlanFormValues>({
    defaultValues: defaultValues ?? buildDefaultFormValues(),
  });

  const onSubmit = useCallback(
    async (values: SeasonalPlanFormValues) => {
      const monthlyMultipliers = MONTH_KEYS.reduce<Partial<MonthlyMultipliers>>((acc, key) => {
        acc[key] = Number(values.multipliers[key]);
        return acc;
      }, {}) as MonthlyMultipliers;

      const adminFee = values.adminFeeMonthly.trim();
      const isEquivalent = sumMultipliers(values.multipliers) === ANNUAL_EQUIVALENT_TOTAL;

      const body = {
        name: values.name.trim(),
        monthlyMultipliers,
        ...(adminFee === '' ? {} : { adminFeeMonthly: Number(adminFee) }),
        ...(isEquivalent ? {} : { acknowledgeException: values.acknowledgeException }),
      };

      try {
        if (planGroupId) {
          await createVersion({ planGroupId, ...body }).unwrap();
          toast.success('New version published');
          closeModal();
        } else {
          const created = await createPlan({ ...body, programType }).unwrap();
          toast.success('Seasonal plan created');
          onSuccess?.(created.planGroupId);
          closeModal();
        }
      } catch (e: any) {
        toast.error(e?.data?.message || 'Failed to save seasonal plan');
      }
    },
    [planGroupId, createVersion, createPlan, programType, closeModal, onSuccess],
  );

  const handleCancel = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const modalId = isVersionMode ? 'publish-seasonal-version' : 'create-seasonal-plan';
  const title = isVersionMode
    ? `Publish New Version${defaultValues?.name ? ` — ${defaultValues.name}` : ''}`
    : 'New Seasonal Plan';

  return (
    <Modal ariaLabelledBy={modalId} isOpen={isModalOpen} onDismiss={closeModal} maxWidth="900px">
      <Modal.Header id={modalId} title={title} onDismiss={closeModal} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body background="secondary">
          <Box direction="column" childGap="md">
            <Box margin="0 0 lg 0">
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Plan name is required' }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="plan-name"
                    label="Plan Name"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isRequired
                    error={fieldState.error?.message}
                  />
                )}
              />
            </Box>

            <Box direction="column" childGap="sm">
              <Box fontWeight="medium">Monthly Multipliers</Box>
              <Box fontSize="sm" color="body-secondary">
                Each month is a whole percentage of the standard monthly payment. 100% leaves the payment unchanged.
              </Box>
              {/* Column-major fill: Jan–Jun down the first column, Jul–Dec down the second. */}
              <div className="grid grid-flow-col grid-cols-2 grid-rows-6 gap-12">
                {MONTH_KEYS.map((monthKey) => (
                  <MonthMultiplierInput key={monthKey} monthKey={monthKey} control={control} />
                ))}
              </div>
            </Box>

            <AnnualTotalSummary control={control} />

            <Controller
              name="adminFeeMonthly"
              control={control}
              rules={{
                validate: (value: string) => {
                  if (value.trim() === '') return true;
                  const parsed = Number(value);
                  if (Number.isNaN(parsed)) return 'Must be a number';
                  if (parsed < 0) return 'Cannot be negative';
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <TextInput
                  id="admin-fee-monthly"
                  label="Admin Fee (optional)"
                  prefix="$"
                  suffix="/mo"
                  inputMode="decimal"
                  helpText="Flat monthly fee charged alongside the seasonal schedule."
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <ExceptionAcknowledgement control={control} />
          <Button
            type="button"
            onClick={handleCancel}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" isLoading={isSubmitting}>
            {isVersionMode ? 'Publish Version' : 'Create Plan'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
